import { useCallback } from 'react';
import {
  setName,
  setLogin,
  setFloor,
  setPhone,
  setIntercom,
  setUserName,
  setPhotoUrl,
  setProvider,
  setApartement,
  setEntranceway,
  useAppSelector,
  useAppDispatch,
  setDeliveryAddress,
  setAgreementsAccepted,
  setAreManualCoordinates,
  setIsSubscribedToMailings,
  setPreviouslyAcceptedClientDocuments,
} from '@/app/store';
import { clearGuestSessionId, setTokens, useTokenRefresh } from '@/shared/lib';
import type { ClientProfileDto, TokenPairDto, UserLanguageCode } from '@/app/store/api/restApi';

export type AuthPayload = {
  profile: ClientProfileDto;
  auth: TokenPairDto;
};

export type AppliedAuthResult = {
  userLanguage: UserLanguageCode;
};

export const useApplyAuthPayload = () => {
  const dispatch = useAppDispatch();
  const { scheduleRefreshTimer } = useTokenRefresh();
  const { name: orderName, phone, delivery } = useAppSelector((state) => state.order);

  return useCallback(
    (payload: AuthPayload): AppliedAuthResult => {
      setTokens(payload.auth);
      scheduleRefreshTimer(payload.auth.expiresInSeconds);

      clearGuestSessionId();

      const { profile } = payload;
      const identity = profile.authIdentities[0];

      dispatch(setUserName(profile.name ?? undefined));
      if (orderName === '' && profile.name) dispatch(setName(profile.name));
      if (phone === '' && profile.phone) dispatch(setPhone(profile.phone));
      dispatch(setLogin(identity?.login ?? profile.verifiedEmail ?? undefined));
      dispatch(setPhotoUrl(identity?.providerPhotoUrl ?? undefined));
      dispatch(setProvider(identity?.provider ?? undefined));
      dispatch(setAgreementsAccepted(profile.innerUserInfo.acceptedClientDocuments));
      dispatch(setIsSubscribedToMailings(profile.innerUserInfo.isSubscribedToMailings));
      dispatch(setPreviouslyAcceptedClientDocuments(profile.innerUserInfo.hasPreviouslyAcceptedClientDocuments));

      if (!delivery.address && profile.deliveryAddress) {
        const pd = profile.deliveryAddress;
        dispatch(
          setDeliveryAddress({
            address: pd.address,
            coordinates: { lat: pd.point.lat, lng: pd.point.lng },
            city: pd.city ?? '',
            street: pd.street ?? '',
            house: pd.house ?? '',
          }),
        );
        dispatch(setAreManualCoordinates(pd.areManualCoordinates));
        if (pd.floor) dispatch(setFloor(pd.floor));
        if (pd.entranceway) dispatch(setEntranceway(pd.entranceway));
        if (pd.apartment) dispatch(setApartement(pd.apartment));
        if (pd.intercom) dispatch(setIntercom(pd.intercom));
      }

      return { userLanguage: profile.languageCode };
    },
    [scheduleRefreshTimer, dispatch, orderName, phone, delivery.address],
  );
};
